import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import type { Perfil } from "@/lib/perfiles";
import { esRutValido, normalizarRut } from "@/lib/rut";

/* Campos del formulario. `perfil_id` viaja como string porque eso es lo que
   entrega un <select> nativo; se convierte a número al enviar. */
const camposBase = {
  first_name: z.string().trim().min(1, "El nombre es obligatorio."),
  last_name: z.string().trim().min(1, "El apellido es obligatorio."),
  rut: z
    .string()
    .trim()
    .min(1, "El RUT es obligatorio.")
    .refine(esRutValido, "El RUT no es válido: revisa el dígito verificador."),
  cargo: z.string().trim().min(1, "El cargo es obligatorio."),
  /* OPCIONAL, y el único del formulario que lo es. Sin `min(1)` a propósito:
     es un canal de contacto, no la identidad de la persona, y exigirlo llenaría
     el maestro de números inventados que después el cliente marca. Tampoco
     valida formato — entran anexos y prefijos que cada uno escribe distinto. */
  telefono: z.string().trim().max(50, "El teléfono no puede superar los 50 caracteres."),
  email: z.email("Ingresa un correo válido."),
  email_confirmation: z.string(),
  perfil_id: z.string(),
};

const usuarioSchema = z.object(camposBase);

export type UsuarioValues = z.infer<typeof usuarioSchema>;

/** Campos que el 422 del backend puede señalar y el formulario sabe resaltar. */
export const CAMPOS_DE_USUARIO = [
  "first_name",
  "last_name",
  "rut",
  "cargo",
  "telefono",
  "email",
  "email_confirmation",
  "perfil_id",
] as const;

export type CampoDeUsuario = (typeof CAMPOS_DE_USUARIO)[number];

/**
 * Las dos pantallas piden perfil (obligatorio en ambas); solo el alta pide
 * además confirmar el correo. La validación se arma según eso para no exigir
 * un campo que la pantalla no muestra: `perfil_id` se exige únicamente cuando
 * el formulario recibió la lista de perfiles y por lo tanto dibujó el select.
 */
function construirSchema(pideConfirmacion: boolean, pidePerfil: boolean) {
  return usuarioSchema
    .superRefine((valores, ctx) => {
      if (pideConfirmacion && valores.email !== valores.email_confirmation) {
        ctx.addIssue({
          code: "custom",
          path: ["email_confirmation"],
          message: "Los correos no coinciden.",
        });
      }

      if (pidePerfil && !valores.perfil_id) {
        ctx.addIssue({ code: "custom", path: ["perfil_id"], message: "El perfil es obligatorio." });
      }
    });
}

interface UsuarioFormularioProps {
  valoresIniciales?: Partial<UsuarioValues>;
  /** Alta: pide confirmar el correo. */
  pideConfirmacionDeCorreo?: boolean;
  /** Lista de perfiles asignables (del maestro de perfiles). */
  perfiles?: Perfil[];
  /**
   * Motivo por el que el perfil NO se puede tocar en esta ficha. El backend
   * rechaza con 422 que un Super Admin cambie su propio perfil; la pantalla
   * evita el choque en vez de dejar mandar algo que ya se sabe rechazado.
   */
  perfilBloqueado?: string;
  etiquetaEnvio: string;
  enviando: boolean;
  onEnviar: (
    valores: UsuarioValues,
    acciones: { errorDeCampo: (campo: CampoDeUsuario, mensaje: string) => void },
  ) => void;
  onCancelar?: () => void;
}

/**
 * Ficha de usuario (pantallas "Crear usuario" y "Editar usuario" del
 * prototipo): identidad + RUT + cargo + correo + teléfono, en la grilla de dos
 * columnas del prototipo, y el perfil abajo.
 *
 * El teléfono es el único campo OPCIONAL, y el único que sale del sistema: lo
 * ve el cliente en su portal y en los dos correos. Vacío no es un dato
 * faltante — el `""` que manda el input se convierte en `null` y esas
 * pantallas omiten la línea en vez de dejar un rótulo solo.
 */
export function UsuarioFormulario({
  valoresIniciales,
  pideConfirmacionDeCorreo = false,
  perfiles,
  perfilBloqueado,
  etiquetaEnvio,
  enviando,
  onEnviar,
  onCancelar,
}: UsuarioFormularioProps) {
  const schema = useMemo(
    () => construirSchema(pideConfirmacionDeCorreo, Boolean(perfiles)),
    [pideConfirmacionDeCorreo, perfiles],
  );

  const form = useForm<UsuarioValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: "",
      last_name: "",
      rut: "",
      cargo: "",
      telefono: "",
      email: "",
      email_confirmation: "",
      perfil_id: "",
      ...valoresIniciales,
    },
  });

  function handleSubmit(valores: UsuarioValues) {
    onEnviar(
      // El RUT sale normalizado (`12345678-9`) para que la unicidad del
      // backend compare contra la misma forma canónica que persiste.
      { ...valores, rut: normalizarRut(valores.rut) },
      {
        errorDeCampo: (campo, mensaje) =>
          form.setError(campo, { type: "server", message: mensaje }),
      },
    );
  }

  return (
    <div className="bg-card w-full max-w-[40rem] rounded-xl border p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre</FormLabel>
                  <FormControl>
                    <Input placeholder="María" autoComplete="given-name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Apellido</FormLabel>
                  <FormControl>
                    <Input placeholder="González" autoComplete="family-name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="rut"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RUT</FormLabel>
                  <FormControl>
                    <Input placeholder="12.345.678-5" autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>Con o sin puntos: se guarda como 12345678-5.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cargo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cargo</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Tasadora agrícola"
                      autoComplete="organization-title"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Los DOS canales de contacto, juntos y en la misma fila. La
              confirmación del correo cae sola en la columna de la izquierda
              —o sea justo debajo del correo, no del teléfono—, que es donde
              se la busca. */}
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Correo electrónico</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="maria@consultek.cl"
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="telefono"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Teléfono (opcional)</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+56 9 8123 4567"
                      autoComplete="tel"
                      {...field}
                    />
                  </FormControl>
                  {/* Dice a QUIÉN se lo estamos mostrando, que es lo que
                      cambia la decisión de cargarlo o no. "Opcional" a secas
                      no alcanza: quien lo lea así lo deja vacío sin saber que
                      es el número al que el cliente llamaría. */}
                  <FormDescription>
                    Si lo cargas, el cliente lo ve en su portal y en los correos.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {pideConfirmacionDeCorreo && (
              <FormField
                control={form.control}
                name="email_confirmation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmar correo electrónico</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="maria@consultek.cl"
                        autoComplete="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {perfiles && (
            <FormField
              control={form.control}
              name="perfil_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Perfil de usuario</FormLabel>
                  <FormControl>
                    <NativeSelect disabled={Boolean(perfilBloqueado)} {...field}>
                      <NativeSelectOption value="">Elige un perfil</NativeSelectOption>
                      {perfiles.map((perfil) => (
                        <NativeSelectOption key={perfil.id} value={String(perfil.id)}>
                          {perfil.nombre}
                        </NativeSelectOption>
                      ))}
                    </NativeSelect>
                  </FormControl>
                  {perfilBloqueado && <FormDescription>{perfilBloqueado}</FormDescription>}
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={enviando} className="gap-2">
              {enviando && <Spinner className="text-primary-foreground" />}
              {etiquetaEnvio}
            </Button>
            {onCancelar && (
              <Button type="button" variant="outline" onClick={onCancelar}>
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
